import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    ProjectNote,
    FolderAttachment,
    DocumentContact,
    Project,
} from 'common/types';
import { initialProject } from 'common/stores/redux/Project/constants';
import { UpdateKeyValuePairPayload } from 'common/stores/redux/interfaces';

export interface ProjectSliceState {
    initialized: boolean;
    project: Project;
}

const initialState: ProjectSliceState = {
    initialized: false,
    project: initialProject,
};

const projectSlice = createSlice({
    name: 'project',
    initialState,
    reducers: {
        /**
         * Initialize the project state with a project
         */
        init(
            state: ProjectSliceState,
            action: PayloadAction<ProjectSliceState['project']>
        ) {
            const project = action.payload;
            return {
                ...state,
                initialized: true,
                project,
            };
        },

        /**
         * Replace the entire project
         */
        replace(
            state: ProjectSliceState,
            action: PayloadAction<ProjectSliceState['project']>
        ) {
            return {
                ...state,
                project: action.payload,
            };
        },

        /**
         * Add a note to the project's notes array
         */
        addNote(state: ProjectSliceState, action: PayloadAction<ProjectNote>) {
            return {
                ...state,
                project: {
                    ...state.project,
                    notes: [...(state.project.notes ?? []), action.payload],
                },
            };
        },

        /**
         * Update a note in the project's notes array
         */
        updateNote(
            state: ProjectSliceState,
            action: PayloadAction<ProjectNote>
        ) {
            const notes = [...(state.project.notes ?? [])];
            const idx = notes.findIndex(note => note.id === action.payload.id);

            if (idx !== -1) {
                notes[idx] = action.payload;
                return {
                    ...state,
                    project: { ...state.project, notes },
                };
            }

            return state;
        },

        /**
         * Add a contact to the project's contacts array
         */
        addContact(
            state: ProjectSliceState,
            action: PayloadAction<DocumentContact>
        ) {
            return {
                ...state,
                project: {
                    ...state.project,
                    contacts: [
                        ...(state.project.contacts ?? []),
                        action.payload,
                    ],
                },
            };
        },

        /**
         * Update a contact in the project's contacts array
         */
        updateContact(
            state: ProjectSliceState,
            action: PayloadAction<DocumentContact>
        ) {
            const contacts = [...(state.project.contacts ?? [])];
            const idx = contacts.findIndex(
                contact => contact.id === action.payload.id
            );

            if (idx !== -1) {
                contacts[idx] = action.payload;
                return {
                    ...state,
                    project: { ...state.project, contacts },
                };
            }

            return state;
        },

        /**
         * Remove a contact by id from the project's contacts array
         */
        removeContact(state: ProjectSliceState, action: PayloadAction<number>) {
            const contacts = [...(state.project.contacts ?? [])];
            const idx = contacts.findIndex(
                contact => contact.id === action.payload
            );

            if (idx !== -1) {
                contacts.splice(idx, 1);
                return {
                    ...state,
                    project: { ...state.project, contacts },
                };
            }

            return state;
        },

        /**
         * Add a folder attachment to the project's folder
         */
        addFolderAttachment(
            state: ProjectSliceState,
            action: PayloadAction<FolderAttachment>
        ) {
            return {
                ...state,
                project: {
                    ...state.project,
                    folder: {
                        ...(state.project.folder ?? {}),
                        folderAttachments: [
                            ...(state.project.folder?.folderAttachments ?? []),
                            action.payload,
                        ],
                    },
                },
            };
        },

        /**
         * Remove a scope by id from the project's scopes array
         */
        removeScope(
            state: ProjectSliceState,
            action: PayloadAction<{ id: number }>
        ) {
            const scopes = [...(state.project.scopes ?? [])];
            const idx = scopes.findIndex(
                scope => scope.id === action.payload.id
            );

            if (idx !== -1) {
                scopes.splice(idx, 1);
                return {
                    ...state,
                    project: { ...state.project, scopes },
                };
            }

            return state;
        },

        /**
         * Update a single or multiple properties
         */
        updateKeyValuePair(
            state,
            action: PayloadAction<UpdateKeyValuePairPayload>
        ) {
            const { key, value } = action.payload;
            const update: Record<string, unknown> = {};

            if (Array.isArray(key)) {
                key.forEach((k, idx) => {
                    update[k] = value[idx];
                });
            } else {
                update[key] = value;
            }

            return {
                ...state,
                project: { ...state.project, ...update },
            };
        },
    },
});

export const {
    init,
    replace,
    addNote,
    updateNote,
    addContact,
    updateContact,
    removeContact,
    addFolderAttachment,
    removeScope,
    updateKeyValuePair,
} = projectSlice.actions;

export default projectSlice.reducer;
